import {
  Accordion,
  Button,
  ClipboardIcon,
  Collapsible,
  Dialog,
  Flex,
  IconButton,
  InputContainer,
  MarkdownEditor,
  Menu,
  Panel,
  SimpleGrid,
  Switch,
  Text,
  TextField,
  TextFieldProps,
  ToggleButton,
  TrashIcon,
  XMarkIcon,
  toaster,
} from 'components'
import KitIcon, { kitIcons } from 'components/KitIcon'
import { MediaInput } from 'components/media'
import { startCase } from 'lodash-es'
import { useState } from 'react'
import {
  FlexLayoutItem,
  ICarouselLayout,
  IFlexLayout,
  IGridLayout,
  ILayout,
  IMediaLayout,
  ISelectedTraitLayout,
  ITabsLayout,
  ITextLayout,
  IToolLayout,
  ITraitLayout,
  LayoutTitle,
} from 'types'
import { keyFromPath, safelyRemoveLayout } from '../../../utils/templateBuilderUtils'
import { useTools } from '../../../utils/toolUtils'
import { useTemplate } from '../TemplateProvider'
import CopyPasteLayout from './CopyPasteLayout'
import { useTemplateLayout } from './TemplateLayoutProvider'

interface Props {}

export default function LayoutEditor({}: Props) {
  const { template, updateTemplate } = useTemplate()
  const { parentLayout, rootLayout, selectedLayout, setSelectedKeyPath, setLayout } =
    useTemplateLayout()

  const selectedKey = selectedLayout && keyFromPath(selectedLayout.keyPath)
  const isAction = parentLayout?.action?.layoutKey === selectedKey
  const isRoot = selectedLayout?.keyPath === rootLayout?.keyPath

  function handleDelete() {
    if (!selectedLayout || !template?.layoutMap) return

    const layoutMap = safelyRemoveLayout(template, selectedLayout.keyPath)
    if (layoutMap) updateTemplate({ layoutMap })
    setSelectedKeyPath(parentLayout?.keyPath)
  }

  return (
    <Panel key={selectedKey} overflow="auto" px={0} py={4} width="300px">
      {selectedKey ? (
        <>
          <Flex justifyContent="space-between" alignItems="center">
            <Text px={4} textStyle="h5">
              Edit Layout
            </Text>

            <Flex gap={2}>
              <CopyPasteLayout />
              {!isRoot && (
                <IconButton
                  size="sm"
                  aria-label="Delete layout"
                  icon={<TrashIcon size="sm" />}
                  onClick={handleDelete}
                />
              )}
            </Flex>
          </Flex>

          <Flex direction="column" gap={2}>
            <Flex alignItems="center" gap={2} px={4}>
              <Text>Layout key:</Text>
              <Text fontWeight="semibold">{selectedKey}</Text>
              <IconButton
                aria-label="Copy layout key"
                icon={<ClipboardIcon size="sm" type="outline" />}
                onClick={() => {
                  navigator.clipboard.writeText(selectedKey.toString())
                  toaster.create({ title: 'Key copied', type: 'success' })
                }}
              />
            </Flex>

            {isAction && (
              <Input
                inputContainerProps={{ px: 4 }}
                label="Action Title"
                value={parentLayout?.action!.title?.en || ''}
                onChange={en => {
                  //@ts-ignore
                  setLayout({ ...parentLayout, action: { ...parentLayout.action!, title: { en } } })
                }}
                placeholder="Action title"
              />
            )}

            <Accordion
              contentProps={{ display: 'flex', flexDirection: 'column', gap: 4, px: 4 }}
              defaultValue={parentLayout?.type === 'flex' ? ['parent'] : undefined}
              items={[
                ...(parentLayout && 'layouts' in parentLayout
                  ? [{ content: <ParentEditor />, title: 'Parent', value: 'parent' }]
                  : []),
                { content: <TitleEditor />, title: 'Title', value: 'title' },
                { content: <StyleEditor />, title: 'Style', value: 'style' },
                {
                  content: <EditorSwitch />,
                  title: startCase(selectedLayout.type),
                  value: 'specific',
                },
              ]}
            />
          </Flex>
        </>
      ) : null}
    </Panel>
  )
}

function ParentEditor() {
  const { selectedLayout, parentLayout, setLayout } = useTemplateLayout()
  if (!selectedLayout || !parentLayout || !('layouts' in parentLayout)) return null

  const key = keyFromPath(selectedLayout.keyPath)
  const layoutItem = parentLayout.layouts?.find(l => l.key === key)
  if (!layoutItem) return null

  const updateLayoutItem = (updates: Partial<FlexLayoutItem>) => {
    const updatedLayouts = parentLayout.layouts?.map(l =>
      l.key === key ? { ...l, ...updates } : l
    )
    setLayout({ ...parentLayout, layouts: updatedLayouts })
  }

  return (
    <>
      {parentLayout.type === 'tabs' && (
        <Input
          label="Tab Title"
          value={layoutItem.tabTitle?.en || selectedLayout.title?.text.en || ''}
          onChange={en => updateLayoutItem({ tabTitle: { en } })}
          placeholder="Tab title"
        />
      )}

      {parentLayout.type === 'flex' && (
        <>
          <NumberInput
            label="Flex Basis"
            value={Number((layoutItem as FlexLayoutItem).basis?.replace('%', '')) || undefined}
            onChange={val => {
              const basis = val ? (`${val}%` as `${number}%`) : undefined
              updateLayoutItem({ basis })
            }}
            placeholder="Percentage (e.g. 50)"
            rightElement={
              <Text color="gray.500" pr={2}>
                %
              </Text>
            }
          />

          <NumberInput
            label="Flex Grow"
            value={Number((layoutItem as FlexLayoutItem).grow) || undefined}
            onChange={grow => updateLayoutItem({ grow })}
            placeholder="Growth factor (e.g. 1)"
          />
        </>
      )}
    </>
  )
}

function TitleEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  const { icon, text, iconFill } = selectedLayout.title || {}

  const updateTitle = (updates: Partial<LayoutTitle>) => {
    setLayout({ ...selectedLayout, title: { ...selectedLayout.title, ...updates } as LayoutTitle })
  }

  const { title, ...layoutWithoutTitle } = selectedLayout

  return (
    <>
      <Flex alignItems="center" gap={2}>
        <Input
          label="Title Text"
          value={text?.en || ''}
          onChange={en => updateTitle({ text: { en } })}
          placeholder="Title text"
        />
        <IconButton
          icon={<XMarkIcon />}
          mt="20px"
          size="sm"
          aria-label="Clear title"
          onClick={() => setLayout(layoutWithoutTitle)}
        />
      </Flex>

      <Flex alignItems="center" gap={2}>
        <Menu
          contentProps={{ maxHeight: '300px', overflowY: 'auto' }}
          label="Icon"
          portal
          alignSelf="flex-start"
          variant="outline"
          onChange={icon => updateTitle({ icon })}
          value={icon}
          options={kitIcons.map(icon => ({
            value: icon,
            label: (
              <Flex gap={2} alignItems="center">
                <KitIcon type={icon} size={24} />
                <Text>{startCase(icon)}</Text>
              </Flex>
            ),
          }))}
        />
        <IconButton
          icon={<XMarkIcon />}
          mt="20px"
          size="sm"
          aria-label="Clear icon"
          onClick={() => updateTitle({ icon: undefined })}
        />
      </Flex>

      <Collapsible alignSelf="flex-start" open={!!icon}>
        <ToggleButton
          label="Icon Fill"
          value={iconFill || 'white'}
          onChange={value => updateTitle({ iconFill: value === 'white' ? undefined : value })}
          options={['white', 'primary'] as const}
        />
      </Collapsible>
    </>
  )
}

function StyleEditor() {
  const { parentLayout, selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  const { width, height, borderRadius } = selectedLayout.style || {}
  const { background } = selectedLayout.style || {}

  const heightNumber = typeof height === 'string' ? parseInt(height) : height
  const widthNumber = typeof width === 'string' ? parseInt(width) : width
  const backgroundType =
    background?.mediaId != null ? 'image' : background?.color != null ? 'color' : 'none'
  const carouselItem = parentLayout?.type === 'carousel'

  function updateStyle(updates: Partial<ILayout['style']>) {
    setLayout({ ...selectedLayout!, style: { ...selectedLayout!.style, ...updates } })
  }

  const updateBackground = (updates: Partial<typeof background>) => {
    const updatedStyle = { ...selectedLayout.style, background: { ...background, ...updates } }
    setLayout({ ...selectedLayout, style: updatedStyle })
  }

  return (
    <>
      {!carouselItem && (
        <>
          <NumberInput
            label="Width"
            rightElement={
              <Menu
                value={typeof width === 'string' ? '%' : 'px'}
                variant="ghost"
                onChange={v =>
                  updateStyle({ width: v === 'px' ? widthNumber : `${widthNumber || 50}%` })
                }
                portal
                options={[
                  { value: 'px', label: 'px' },
                  { value: '%', label: '%' },
                ]}
              />
            }
            inputGroupProps={{ endElementProps: { p: 0 } }}
            style={{ paddingInlineEnd: '70px' }}
            value={widthNumber}
            onChange={val => {
              updateStyle({
                width: val ? (typeof width === 'string' ? `${val}%` : val) : undefined,
              })
            }}
          />

          <NumberInput
            label="Height"
            rightElement={
              <Menu
                value={typeof height === 'string' ? '%' : 'px'}
                variant="ghost"
                onChange={v =>
                  updateStyle({ height: v === 'px' ? heightNumber : `${heightNumber || 50}%` })
                }
                portal
                options={[
                  { value: 'px', label: 'px' },
                  { value: '%', label: '%' },
                ]}
              />
            }
            inputGroupProps={{ endElementProps: { p: 0 } }}
            style={{ paddingInlineEnd: '70px' }}
            value={heightNumber}
            onChange={val => {
              updateStyle({
                height: val ? (typeof height === 'string' ? `${val}%` : val) : undefined,
              })
            }}
          />

          <NumberInput
            label="Border Radius"
            value={borderRadius}
            onChange={borderRadius => {
              updateStyle({ ...selectedLayout.style, borderRadius })
            }}
          />
        </>
      )}

      <PaddingEditor />

      <ToggleButton
        alignSelf="flex-start"
        label="Background"
        value={backgroundType}
        onChange={value => {
          switch (value) {
            case 'none':
              updateBackground({ color: undefined, mediaId: undefined, blur: undefined })
              break
            case 'color':
              updateBackground({ mediaId: undefined, color: 'rgba(0, 0, 0, .5)' })
              break
            case 'image':
              updateBackground({ color: undefined, mediaId: '' })
              break
          }
        }}
        options={['none', 'color', 'image'] as const}
      />

      <Collapsible open={backgroundType === 'color'}>
        <Input
          label="Background Color"
          value={background?.color || ''}
          onChange={color => updateBackground({ color })}
        />
      </Collapsible>

      <Collapsible open={backgroundType === 'image'}>
        <MediaInput
          label="Background Image"
          id={background?.mediaId || undefined}
          type="image"
          onSelect={mediaId => updateBackground({ mediaId })}
          onClear={() => updateBackground({ mediaId: undefined })}
        />
      </Collapsible>

      <Collapsible open={backgroundType !== 'none'}>
        <NumberInput
          label="Blur"
          value={background?.blur}
          onChange={blur => updateBackground({ blur })}
          placeholder="Blur amount"
        />
      </Collapsible>
    </>
  )
}

function EditorSwitch() {
  const { selectedLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  switch (selectedLayout.type) {
    case 'carousel':
      return <CarouselEditor />
    case 'flex':
      return <FlexEditor />
    case 'grid':
      return <GridEditor />
    case 'media':
      return <MediaEditor />
    case 'selected-traits':
      return <SelectedTraitsEditor />
    case 'tabs':
      return <TabsEditor />
    case 'text':
      return <TextEditor />
    case 'tool':
      return <ToolEditor />
    case 'trait':
      return <TraitEditor />
    default:
      return null
  }
}

function CarouselEditor() {
  const { template } = useTemplate()
  const { selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout || !template?.traitMap) return null

  const { layouts } = selectedLayout as ICarouselLayout

  const groupKeyIndex = layouts.findIndex(l => !!l.traitGroupKey)
  const traitLayout = layouts[groupKeyIndex]
  const { traitGroupKey } = traitLayout || {}

  const updateLayout = (updates: Partial<ILayout>) => {
    setLayout({ ...selectedLayout, ...updates } as any)
  }

  return (
    <Flex alignItems="flex-end" gap={2}>
      <Menu
        label="Trait Group"
        value={traitGroupKey}
        placeholder="Render selected group traits"
        onChange={key => {
          if (traitGroupKey != null) {
            updateLayout({
              layouts: layouts.map((layout, index) =>
                index === groupKeyIndex ? { ...layout, traitGroupKey: key } : layout
              ),
            })
          } else {
            updateLayout({ layouts: [...layouts, { traitGroupKey: key }] })
          }
        }}
        options={Object.entries(template.traitMap).map(([key, group]) => ({
          value: Number(key),
          label: group.title?.en,
        }))}
      />
      {traitGroupKey && (
        <IconButton
          icon={<TrashIcon />}
          aria-label="Remove Trait Group"
          onClick={() =>
            updateLayout({
              layouts: layouts.map((layout, index) =>
                index === groupKeyIndex ? { ...layout, traitGroupKey: undefined } : layout
              ),
            })
          }
        />
      )}
    </Flex>
  )
}

function TextEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  const updateLayout = (updates: Partial<ILayout>) => {
    setLayout({ ...selectedLayout, ...updates } as any)
  }

  const { text, align, justify, scroll, size } = selectedLayout as ITextLayout
  return (
    <>
      <Dialog modal={false} size="lg" trigger={<Button variant="outline">Edit Text</Button>}>
        <MarkdownEditor
          debounce={500}
          value={text?.en || ''}
          onChange={en => updateLayout({ text: { en } })}
          tagText
        />
      </Dialog>

      <Menu
        label="Text Alignment"
        value={align}
        placeholder="Left"
        onChange={align => updateLayout({ align })}
        options={[
          { value: 'left', label: 'Left' },
          { value: 'center', label: 'Center' },
          { value: 'right', label: 'Right' },
        ]}
      />

      <Menu
        label="Text Justification"
        value={justify}
        placeholder="Top"
        onChange={justify => updateLayout({ justify })}
        options={[
          { value: 'top', label: 'Top' },
          { value: 'center', label: 'Center' },
          { value: 'bottom', label: 'Bottom' },
        ]}
      />

      <NumberInput
        label="Font Size"
        value={size}
        onChange={size => updateLayout({ size })}
        placeholder="18"
      />

      <Switch
        label="Scrollable"
        checked={scroll || false}
        onChange={checked => updateLayout({ scroll: checked })}
      />
    </>
  )
}

function MediaEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  const updateLayout = (updates: Partial<ILayout>) => {
    setLayout({ ...selectedLayout, ...updates } as any)
  }

  const mediaLayout = selectedLayout as IMediaLayout
  return (
    <>
      <MediaInput
        label="Media"
        id={mediaLayout.mediaId || undefined}
        onSelect={mediaId => updateLayout({ mediaId })}
        onClear={() => updateLayout({ mediaId: undefined })}
      />
    </>
  )
}

function FlexEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  const updateLayout = (updates: Partial<ILayout>) => {
    setLayout({ ...selectedLayout, ...updates } as any)
  }

  const flexLayout = selectedLayout as IFlexLayout
  return (
    <>
      <Menu
        label="Direction"
        value={flexLayout.direction || 'column'}
        onChange={value => updateLayout({ direction: value as 'row' | 'column' })}
        options={[
          { value: 'row', label: 'Row' },
          { value: 'column', label: 'Column' },
        ]}
      />

      <NumberInput
        label="Gap"
        value={flexLayout.gap}
        onChange={gap => updateLayout({ gap })}
        placeholder="8"
      />

      <Menu
        label="Justify Content"
        value={flexLayout.justify || 'flex-start'}
        onChange={value => updateLayout({ justify: value as any })}
        options={[
          { value: 'flex-start', label: 'Flex Start' },
          { value: 'center', label: 'Center' },
          { value: 'flex-end', label: 'Flex End' },
          { value: 'space-between', label: 'Space Between' },
          { value: 'space-around', label: 'Space Around' },
          { value: 'space-evenly', label: 'Space Evenly' },
        ]}
      />

      <Menu
        label="Align Items"
        value={flexLayout.align || 'stretch'}
        onChange={value => updateLayout({ align: value as any })}
        options={[
          { value: 'flex-start', label: 'Flex Start' },
          { value: 'center', label: 'Center' },
          { value: 'flex-end', label: 'Flex End' },
          { value: 'stretch', label: 'Stretch' },
          { value: 'baseline', label: 'Baseline' },
        ]}
      />

      <Switch
        label="Divider"
        checked={flexLayout.divider || false}
        onChange={checked => updateLayout({ divider: checked })}
      />

      <Switch
        label="Scrollable"
        checked={flexLayout.scroll || false}
        onChange={checked => updateLayout({ scroll: checked })}
      />
    </>
  )
}

function GridEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  const updateLayout = (updates: Partial<ILayout>) => {
    setLayout({ ...selectedLayout, ...updates } as any)
  }

  const { columnCount, gap, itemHeight } = selectedLayout as IGridLayout
  return (
    <>
      <NumberInput
        label="Column Count"
        value={columnCount}
        onChange={columnCount => updateLayout({ columnCount })}
        placeholder="2"
      />

      <NumberInput
        label="Gap"
        value={gap}
        onChange={gap => updateLayout({ gap })}
        placeholder="8"
      />

      <NumberInput
        label="Item Height"
        value={itemHeight}
        onChange={itemHeight => updateLayout({ itemHeight })}
        placeholder="Fixed item height"
      />
    </>
  )
}

function TabsEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  const updateLayout = (updates: Partial<ILayout>) => {
    setLayout({ ...selectedLayout, ...updates } as any)
  }

  const tabsLayout = selectedLayout as ITabsLayout
  return (
    <Menu
      label="Orientation"
      value={tabsLayout.orientation || 'horizontal'}
      onChange={value => updateLayout({ orientation: value as 'horizontal' | 'vertical' })}
      options={[
        { value: 'horizontal', label: 'Horizontal' },
        { value: 'vertical', label: 'Vertical' },
      ]}
    />
  )
}

function SelectedTraitsEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  const updateLayout = (updates: Partial<ILayout>) => {
    setLayout({ ...selectedLayout, ...updates } as any)
  }

  const traitsLayout = selectedLayout as ISelectedTraitLayout
  return (
    <>
      <NumberInput
        label="Trait Group Key"
        value={traitsLayout.traitGroupKey}
        onChange={traitKey => updateLayout({ traitGroupKey: traitKey })}
        placeholder="Trait group key"
      />

      <Input
        label="Group Title"
        value={traitsLayout.groupTitle?.en || ''}
        onChange={en => {
          updateLayout({ groupTitle: { en } })
        }}
        placeholder="Group title"
      />

      <Switch
        label="Learn More"
        checked={traitsLayout.learnMore || false}
        onChange={checked => updateLayout({ learnMore: checked })}
      />
    </>
  )
}

function ToolEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()
  const { tools, status } = useTools()

  if (!selectedLayout) return null

  const updateLayout = (updates: Partial<ILayout>) => {
    setLayout({ ...selectedLayout, ...updates } as any)
  }

  const toolLayout = selectedLayout as IToolLayout
  const hasTools = tools.length > 0
  return (
    <Menu
      label="Tool"
      portal
      value={toolLayout.toolId}
      placeholder={hasTools ? 'Select a tool' : 'No tools available'}
      onChange={toolId => updateLayout({ toolId })}
      disabled={status === 'loading' || !hasTools}
      options={
        tools.map(tool => ({
          value: tool.id,
          label: (
            <Flex direction="column">
              <Text fontWeight="medium" lineClamp={1}>
                {tool.title}
              </Text>
              <Text textStyle="bodyXSmall" color="gray.400" lineClamp={1}>
                {tool.url}
              </Text>
            </Flex>
          ),
        })) ?? []
      }
    />
  )
}

function TraitEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()

  if (!selectedLayout) return null

  const updateLayout = (updates: Partial<ILayout>) => {
    setLayout({ ...selectedLayout, ...updates } as any)
  }

  const traitLayout = selectedLayout as ITraitLayout
  return (
    <>
      <NumberInput
        label="Trait ID"
        value={traitLayout.traitKey}
        onChange={traitKey => updateLayout({ traitGroupKey: traitKey })}
        placeholder="Trait ID"
      />

      <NumberInput
        label="Value Index"
        value={traitLayout.valueIndex}
        onChange={valueIndex => updateLayout({ valueIndex })}
        placeholder="Value Index"
      />
    </>
  )
}

function PaddingEditor() {
  const { selectedLayout, setLayout } = useTemplateLayout()
  const [paddingMode, setPaddingMode] = useState<'1' | '2' | '4'>(
    (selectedLayout?.style?.padding?.length.toString() as '1') ?? '1'
  )

  if (!selectedLayout || selectedLayout.type === 'carousel') return null

  const { padding } = selectedLayout.style || {}
  const [top = 0, right = 0, bottom = 0, left = 0] = padding || []

  function updateStyle(updates: Partial<ILayout['style']>) {
    setLayout({ ...selectedLayout!, style: { ...selectedLayout!.style, ...updates } })
  }

  const updatePadding = (newPadding: number[]) => {
    updateStyle({ padding: newPadding.some(p => p > 0) ? newPadding : undefined })
  }

  const vertical = top
  const horizontal = right

  function handleMode(mode: '1' | '2' | '4') {
    switch (mode) {
      case '1':
        updatePadding([top])
        break
      case '2':
        updatePadding([top, right || top])
        break
      case '4':
        updatePadding([top, right || top, bottom || top, left || right || top])
        break
    }

    setPaddingMode(mode)
  }

  return (
    <InputContainer
      label={
        <Flex alignItems="center" gap={2}>
          <Text textStyle="bodySmall" opacity={0.7}>
            Padding
          </Text>
          <ToggleButton
            value={paddingMode}
            onChange={handleMode}
            options={['1', '2', '4']}
            containerProps={{ borderRadius: 'sm' }}
            buttonProps={{ px: 2, py: 1 }}
          />
        </Flex>
      }
    >
      {paddingMode === '4' ? (
        <SimpleGrid columns={2} gap={2}>
          <NumberInput
            label="Left"
            value={left}
            onChange={v => updatePadding([top, right, bottom, v || 0])}
            placeholder="0"
          />
          <NumberInput
            label="Top"
            value={top}
            onChange={v => updatePadding([v || 0, right, bottom, left])}
            placeholder="0"
          />
          <NumberInput
            label="Right"
            value={right}
            onChange={v => updatePadding([top, v || 0, bottom, left])}
            placeholder="0"
          />
          <NumberInput
            label="Bottom"
            value={bottom}
            onChange={v => updatePadding([top, right, v || 0, left])}
            placeholder="0"
          />
        </SimpleGrid>
      ) : paddingMode === '2' ? (
        <SimpleGrid columns={2} gap={2}>
          <NumberInput
            label="Horizontal"
            value={horizontal}
            onChange={v => updatePadding([vertical, v || 0, vertical, v || 0])}
            placeholder="0"
          />
          <NumberInput
            label="Vertical"
            value={vertical}
            onChange={v => updatePadding([v || 0, horizontal, v || 0, horizontal])}
            placeholder="0"
          />
        </SimpleGrid>
      ) : (
        <NumberInput
          label="All sides"
          value={top}
          onChange={v => updatePadding([v || 0])}
          placeholder="0"
        />
      )}
    </InputContainer>
  )
}

interface InputProps extends Omit<TextFieldProps, 'onChange'> {
  onChange: (value: string) => void
}

function Input({ value, onChange, ...rest }: InputProps) {
  return (
    <TextField
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      onFocus={e => e.target.select()}
      {...rest}
    />
  )
}

interface NumberInputProps extends Omit<TextFieldProps, 'onChange' | 'type' | 'value'> {
  value?: number
  onChange: (value: number | undefined) => void
}

function NumberInput({ onChange, ...rest }: NumberInputProps) {
  return <Input type="number" min={0} onChange={v => onChange(Number(v) || undefined)} {...rest} />
}
